/**
 * Profile edit form (name; phone/email read-only). Used by Dashboard profile tab.
 */
import React, { memo, useCallback } from 'react';
import { FaUser, FaUserEdit, FaPhone, FaEnvelope } from 'react-icons/fa';
import { useI18n } from '../../contexts/I18n';
import { Card, Error, SuccessBanner } from './Card';

const ProfileEditForm = memo(function ProfileEditForm({
  profile,
  draft,
  setDraft,
  setEdit,
  setAvatar,
  setPasswordData,
  setPasswordErrors,
  setPasswordVisibility,
  msg,
  saving,
  onSaveProfile,
  t,
}) {
  const { lang } = useI18n();
  const isRtl = lang === 'ar';
  const rtlTextStyle = isRtl ? { textAlign: 'right', direction: 'rtl', width: '100%', display: 'block' } : {};
  const handleNameChange = useCallback((e) => {
    setDraft((prev) => (prev ? { ...prev, name: e.target.value } : { name: e.target.value }));
  }, [setDraft]);

  const handleCancel = useCallback(() => {
    setEdit(false);
    setDraft(profile);
    setAvatar(null);
    setPasswordData({ currentPwd: '', newPwd: '', confirmPwd: '' });
    setPasswordErrors({});
    setPasswordVisibility({ currentPwd: false, newPwd: false, confirmPwd: false });
  }, [setEdit, setDraft, profile, setAvatar, setPasswordData, setPasswordErrors, setPasswordVisibility]);

  const displayName = (draft?.name || profile?.name || 'U').trim();
  const initials = displayName.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || 'U';

  if (msg?.ok) {
    return (
      <Card extraClass={`profile-card profile--edit profile--edit-success ${isRtl ? 'rtl' : ''}`}>
        <div className={`profile-edit-form profile-edit-form--success ${isRtl ? 'rtl' : ''}`}>
          <SuccessBanner>{msg.ok}</SuccessBanner>
        </div>
      </Card>
    );
  }

  return (
    <Card extraClass={`profile-card profile--edit ${isRtl ? 'rtl' : ''}`}>
      <div className={`profile-edit-form ${isRtl ? 'rtl' : ''}`}>
        <header className={`profile-edit-header ${isRtl ? 'rtl' : ''}`}>
          <div className={`profile-edit-title-row ${isRtl ? 'rtl' : ''}`}>
            <span className="profile-edit-title-icon-wrap" aria-hidden="true">
              <FaUserEdit className="profile-edit-title-icon" size={18} />
            </span>
            <h2 className={`profile-edit-title ${isRtl ? 'rtl' : ''}`} style={rtlTextStyle}>
              {t('editPersonalInformation') || 'Edit Personal Information'}
            </h2>
          </div>
          <p className={`profile-edit-subtitle ${isRtl ? 'rtl' : ''}`} style={rtlTextStyle}>
            {t('updateAccountDetails') || 'Update your account details'}
          </p>
        </header>

        {msg.fail && <Error>{msg.fail}</Error>}

        <div className="profile-edit-hero" aria-hidden="true">
          <div className="profile-edit-avatar">{initials}</div>
          <div className={`profile-edit-hero-copy ${isRtl ? 'rtl' : ''}`}>
            <p className="profile-edit-hero-name">{displayName}</p>
            <p className="profile-edit-hero-hint">
              {isRtl ? 'يمكنك تعديل الاسم فقط' : 'Only your name can be edited'}
            </p>
          </div>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSaveProfile(); }}>
          <div className="profile-edit-grid">
            <div className="profile-edit-field">
              <label className={`profile-edit-label ${isRtl ? 'rtl' : ''}`}>
                <FaUser className="profile-edit-field-icon" aria-hidden="true" />
                {t('name') || 'Name'}
              </label>
              <input
                type="text"
                value={draft?.name ?? ''}
                onChange={handleNameChange}
                className="profile-edit-input"
                placeholder={t('name') || 'Name'}
                autoComplete="name"
              />
            </div>
            <div className="profile-edit-field">
              <label className={`profile-edit-label ${isRtl ? 'rtl' : ''}`}>
                <FaPhone className="profile-edit-field-icon" aria-hidden="true" />
                {t('phone') || 'Phone'}
              </label>
              <input
                type="text"
                value={String(draft?.phone ?? profile?.phone ?? '')}
                readOnly
                disabled
                className="profile-edit-input profile-edit-input-disabled"
                placeholder={t('phone') || 'Phone'}
              />
            </div>
            <div className="profile-edit-field">
              <label className={`profile-edit-label ${isRtl ? 'rtl' : ''}`}>
                <FaEnvelope className="profile-edit-field-icon" aria-hidden="true" />
                {t('email') || 'Email'}
              </label>
              <input
                type="email"
                value={draft?.email ?? ''}
                readOnly
                disabled
                className="profile-edit-input profile-edit-input-disabled"
                placeholder={t('email') || 'Email'}
              />
            </div>
          </div>
          <div className="profile-edit-actions">
            <button type="button" className="profile-cancel-button" onClick={handleCancel}>
              {t('cancel') || 'Cancel'}
            </button>
            <button type="submit" className="profile-save-button" disabled={saving}>
              {saving ? (t('saving') || 'Saving...') : (t('saveChanges') || 'Save')}
            </button>
          </div>
        </form>
      </div>
    </Card>
  );
});

export default ProfileEditForm;
